// apps/client/src/lib/nativeMfa.ts
//
// Thin, typed wrapper around the Firebase modular Web SDK's native
// Multi-Factor Authentication (Google Cloud Identity Platform / GCIP) TOTP API.
//
// This replaces the bespoke `setupMFA` / `verifyAndEnableMFA` / `verifyLoginMFA`
// callables: the second factor is now enrolled and challenged natively by
// Firebase Auth, so the seed never touches our backend (closes the custody
// register item) and the challenge is bound to the session at token issuance.
//
// GCIP allows exactly ONE TOTP factor per account (measured 2026-08-14:
// enrolling a second returns `auth/maximum-second-factor-count-exceeded`), so
// there is deliberately no "add another authenticator" affordance anywhere in
// the client — recovery is an admin-mediated reset, surfaced by MfaRecoveryPanel.

import {
  multiFactor,
  getMultiFactorResolver,
  reauthenticateWithPopup,
  reauthenticateWithCredential,
  GoogleAuthProvider,
  EmailAuthProvider,
  TotpMultiFactorGenerator,
  FactorId,
  type Auth,
  type User,
  type TotpSecret,
  type MultiFactorResolver,
  type MultiFactorError,
  type MultiFactorInfo,
  type UserCredential,
} from 'firebase/auth';
import { MFA_UNSUPPORTED_FACTOR_CODE } from './mfaErrors';

/** Firebase throws this code from `signIn*` when the account has an enrolled factor. */
export const MFA_REQUIRED_CODE = 'auth/multi-factor-auth-required';

/** Human label stored against the enrolled factor (shown in the GCIP console). */
export const MFA_FACTOR_DISPLAY_NAME = 'Authenticator app';

/** TOTP issuer embedded in the otpauth:// URL and the authenticator entry. */
export const MFA_ISSUER = 'VALUTTEN';

/** Narrowing type-guard for the multi-factor-required sign-in error. */
export function isMfaRequiredError(err: unknown): err is MultiFactorError {
  return (
    typeof err === 'object' &&
    err !== null &&
    (err as { code?: unknown }).code === MFA_REQUIRED_CODE
  );
}

/** Build the resolver used to complete a challenge from a caught sign-in error. */
export function buildMfaResolver(auth: Auth, error: MultiFactorError): MultiFactorResolver {
  return getMultiFactorResolver(auth, error);
}

/** Group the shared secret into 4-char blocks so it is readable for manual entry. */
export function formatSecretKey(secretKey: string): string {
  return (secretKey.match(/.{1,4}/g) ?? [secretKey]).join(' ');
}

export interface TotpEnrollmentInit {
  /** Opaque secret handed back to `completeTotpEnrollment` — never persisted by us. */
  secret: TotpSecret;
  /** otpauth:// URL to render as a QR code. */
  otpauthUrl: string;
  /** Shared secret formatted in 4-char blocks for manual authenticator entry. */
  sharedSecretKey: string;
}

/**
 * Start TOTP enrolment: open a multi-factor session, generate a secret, and
 * derive the QR URL + manual key. Requires a recent sign-in — a stale session
 * throws `auth/requires-recent-login`, which the caller surfaces as a re-auth
 * prompt rather than a dead end.
 */
export async function beginTotpEnrollment(user: User): Promise<TotpEnrollmentInit> {
  const mfaUser = multiFactor(user);
  const session = await mfaUser.getSession();
  const secret = await TotpMultiFactorGenerator.generateSecret(session);
  const accountName = user.email ?? user.uid;
  return {
    secret,
    otpauthUrl: secret.generateQrCodeUrl(accountName, MFA_ISSUER),
    sharedSecretKey: formatSecretKey(secret.secretKey),
  };
}

/** Confirm the 6-digit code and enrol the factor against the account. */
export async function completeTotpEnrollment(
  user: User,
  secret: TotpSecret,
  oneTimePassword: string,
  displayName: string = MFA_FACTOR_DISPLAY_NAME,
): Promise<void> {
  const assertion = TotpMultiFactorGenerator.assertionForEnrollment(secret, oneTimePassword);
  await multiFactor(user).enroll(assertion, displayName);
}

/** The enrolled TOTP hint (carries the enrollmentId used to complete a challenge). */
export function getTotpHint(resolver: MultiFactorResolver): MultiFactorInfo | null {
  return resolver.hints.find((hint) => hint.factorId === FactorId.TOTP) ?? null;
}

/** Complete a sign-in challenge with a 6-digit code and the caught error's resolver. */
export async function resolveTotpSignIn(
  resolver: MultiFactorResolver,
  oneTimePassword: string,
): Promise<UserCredential> {
  const hint = getTotpHint(resolver);
  if (!hint) {
    // Only TOTP is offered in this product; a resolver without a TOTP hint means
    // the account carries a factor we do not support in the client. The code
    // lets MfaChallengeCard tell this dead end apart from a wrong code (M3).
    // "the link below" is the card's own "Can't access your authenticator?"
    // button, which opens MfaRecoveryPanel: the only reset route a client user
    // has, and the wording MfaRecoveryPanel already uses.
    throw Object.assign(
      new Error(
        'No authenticator is set up for this account. Request a reset from Valutten using the link below.',
      ),
      { code: MFA_UNSUPPORTED_FACTOR_CODE },
    );
  }
  const assertion = TotpMultiFactorGenerator.assertionForSignIn(hint.uid, oneTimePassword);
  return resolver.resolveSignIn(assertion);
}

/** True when the signed-in user already has a TOTP factor enrolled. */
export function hasEnrolledTotp(user: User): boolean {
  return multiFactor(user).enrolledFactors.some((factor) => factor.factorId === FactorId.TOTP);
}

/**
 * True when the user holds ANY native GCIP second factor.
 *
 * This is the client-side mirror of the assertion the sign-in blocking function
 * makes (`beforeSignInMfaGate.ts`: `enrolledFactors.length >= 1` ⇒ allow), and
 * it is deliberately NOT filtered to TOTP: enrolment must be judged by the same
 * signal that decides whether the user is let in, or the two disagree.
 *
 * Use this — never the legacy `mfaEnabled` custom claim — to answer "is this
 * user enrolled". That claim is written ONLY by the superseded bespoke TOTP flow
 * (`client-functions/src/security/verifyAndEnableMfa.ts`); native enrolment sets
 * a GCIP factor and never sets it, so a natively-enrolled user reads as
 * unenrolled under the claim and gets bounced to /enroll-2fa forever.
 *
 * {@link hasEnrolledTotp} stays TOTP-specific for the enrolment page, which
 * offers exactly one factor type and must not re-enrol an existing TOTP factor.
 */
export function hasEnrolledSecondFactor(user: User): boolean {
  return multiFactor(user).enrolledFactors.length > 0;
}

/**
 * The user's enrolled second factor, or null when they hold none.
 *
 * GCIP allows exactly ONE factor per account in this product (see the file
 * header), so "the first enrolled factor" and "the user's factor" are the same
 * thing; the index is not a truncation of a list we could otherwise show.
 *
 * Exists so surfaces that need to DESCRIBE the factor (Settings shows its
 * display name and enrolment date) read it through the same module that answers
 * "is this user enrolled", rather than importing `multiFactor` themselves and
 * drifting onto a different signal. Returns the raw `MultiFactorInfo`, whose
 * `enrollmentTime` is a UTC date string.
 */
export function getEnrolledSecondFactor(user: User): MultiFactorInfo | null {
  return multiFactor(user).enrolledFactors[0] ?? null;
}

/**
 * Re-authenticate a Google-backed session through the popup.
 *
 * Used to clear `auth/requires-recent-login` in place, so a stale session does
 * not become a dead end at the start of enrolment. Rejects on cancel
 * (`auth/popup-closed-by-user`), a blocked popup, or any SDK failure; the
 * caller falls back to the sign-in-again panel.
 */
export async function reauthenticateWithGoogle(user: User): Promise<void> {
  await reauthenticateWithPopup(user, new GoogleAuthProvider());
}

/**
 * Re-authenticate a password-backed session with a freshly typed password.
 *
 * `user.email` is required to build the credential; an account with a password
 * provider always has one, but the guard keeps a malformed user from producing
 * an opaque SDK error instead of the fallback panel.
 */
export async function reauthenticateWithPassword(user: User, password: string): Promise<void> {
  if (!user.email) {
    throw Object.assign(new Error('No email address on this account.'), {
      code: 'auth/invalid-email',
    });
  }
  await reauthenticateWithCredential(user, EmailAuthProvider.credential(user.email, password));
}
